<template>
  <div
    class="bloomreach-recommendations"
    :class="styleClasses"
  >
    <product-carousel
      :context="context"
      :products="products"
      :tracking-parameters="trackingData"
      :section-id="sectionId"
      :is-cart-popup="isCartPopup"
      :slides-per-view="slidesPerView"
    />
  </div>
</template>

<script>

/**
 * Component: Bloomreach Recommendations
 * ------------------------------------------------------------------------------
 * - Bloomreach recommended products component.
 * @param {String} recommendationId - Engine ID of an existing recommendation model. This ID is created when the engine is saved in Bloomreach Engagement.
 * @param {String} sectionId - Liquid Section's ID.
 * @param {Object} trackingParameters - Object of parameters, typically added to events (Bloomreach, Attraqt, Google).
 * @param {String|Boolean} categoryName - Handle of the collection, used as a Bloomreach attribute.
 * @param {String|Number|Boolean} initialItemId - Product ID, used as a Bloomreach attribute.
 * @param {String} context - Location of the rendered recommendations.
 * @param {Number} recommendationSize - Limit of products to show.
 * @param {Boolean|Object} [slidesPerView] - An object with number of slides to display per `mobile`, `tablet` or `desktop` breakpoints.
 *
 * @emits recommendations-not-loaded - Emitted when recommended products are not loaded.
 */
import { mapState } from 'pinia'
import { nextTick } from 'vue'

import { useCartStore } from '~stores/cart'

import { getProduct } from '~/core/get-product'
import { waitFor } from '~/helpers/dom'

import ProductCarousel from '~components/product-carousel/product-carousel'

export default {
  name: 'BloomreachRecommendations',

  components: {
    ProductCarousel,
  },

  props: {
    recommendationId: {
      type: String,
      default: '',
    },
    trackingParameters: {
      type: Object,
      default: () => ({}),
    },
    sectionId: {
      type: String,
      default: '',
    },
    categoryName: {
      type: [Boolean, String],
      default: false,
    },
    initialItemId: {
      type: [
        Boolean, String, Number,
      ],
      default: false,
    },
    context: {
      type: String,
      default: 'default',
    },
    recommendationSize: {
      type: Number,
      default: 4,
    },
    slidesPerView: {
      type: [Boolean, Object],
      default: false,
    },
  },

  emits: ['recommendations-not-loaded'],

  data() {
    return {
      products: [
        {
          key: 0,
        },
        {
          key: 1,
        },
        {
          key: 2,
        },
        {
          key: 3,
        },
      ],

      /**
       * Reassign the recommendation id to the data of this component to be able
       * to change it dynamically in the future
       */
      recommendation_id: this.recommendationId,

      /**
       * Item ID: the ID of the product that triggered the recommendation.
       * - Prevents given product from appearing in that recommendation
       *   (Example: if on PDP, don't show the current product in recommendations)
       * - https://documentation.bloomreach.com/engagement/docs/integration-of-recommendations#requesting-recommendations
       */
      itemId: false,
    }
  },

  computed: {

    /**
     * Map the Cart.
     */
    ...mapState(useCartStore, ['cartData']),

    /**
     * Calculate whether the component is used inside the cart popup.
     *
     * @returns {Boolean}
     */
    isCartPopup() {
      return this.context === 'cart-popup'
    },

    /**
     * Calculate classes based on the props and location where the component is
     * rendered.
     *
     * @returns {Array<string>} Array of classes to bind to the component.
     */
    styleClasses() {
      return [{ 'is-cart-popup': this.isCartPopup }]
    },

    /**
     * Expands `trackingParameters` prop by adding specific settings related to
     * this particular component.
     *
     * @returns {object} - Object of parameters added to events, indicating the
     * relation to Bloomreach
     */
    trackingData() {
      return {
        ...this.trackingParameters,
        source: 'Bloomreach Recommendations',
        recommendation_id: this.recommendation_id,
      }
    },
  },

  watch: {

    /**
     * Watch for Cart updates.
     */
    cartData() {

      /**
       * Only reload Cart Pop-out Recommendations on cart modification.
       */
      if (
        this.cartData.request.event === 'cart:fetch' ||
        !this.isCartPopup ||
        !this.cartData?.items?.length
      ) {
        return
      }

      /**
       * Find the cart item matching the variant ID of the newest item.
       * - Product ID is not available within the cartData.request object.
       */
      const newItem = this.cartData.items.find(item => {
        return item?.variant_id === this.cartData.request?.data?.id
      })

      this.itemId = newItem?.product_id || false

      this.handleCartPopupRender()

      nextTick(() => {
        this.initializeRecommendations()
      })
    },

    /**
     * Watches changes to the `recommendation_id` property and re-initializes
     * recommendations if the value changes.
     */
    recommendation_id(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.initializeRecommendations()
      }
    },

  },

  mounted () {
    this.itemId = this.initialItemId
    this.initializeRecommendations()
  },

  methods: {

    /**
     * Waits for the `exponea` global variable to become available,
     * then calls the `getRecommendations` method.
     * @async
     * @throws {Error} - If `exponea` variable fails to load
     * @returns {Promise<void>}
     */
    async initializeRecommendations() {
      if (!this.recommendation_id) {
        this.destroyComponent()
        return
      }

      try {

        /**
         * Wait for the `exponea` variable to become available in the global scope
         * before calling the function to retrieve product recommendations.
         * This may occur when the `bloomreach:ready` event is triggered before
         * this component is mounted or when tracking consent is disabled for users
         * and the aforementioned event does not trigger at all.
         */
        if (!exponea.isInitialized) {
          await waitFor('exponea')
        }

        this.getRecommendations()
      }
      catch (error) {
        mint.Log('error', 'Error loading `exponea` variable', error)
      }
    },

    /**
     * Retrieves recommended products from the Bloomreach (exponea) service.
     * - https://documentation.bloomreach.com/engagement/docs/integration-of-recommendations#requesting-recommendations
     * @async
     * @returns {Promise<void>}
     */
    async getRecommendations() {
      const options = {
        recommendationId: this.recommendation_id,
        callback: this.onRecommendationsLoaded,
        size: this.recommendationSize, // Specifies upper limit of the recommendations to return.
        fillWithRandom: true, // If true, fills the recommendations with random items until size is reached.
      }

      if (this.itemId || this.isCartPopup) {
        options.items = {
          [this.itemId]: 1,
        }
      }

      if (this.categoryName && !this.isCartPopup) {
        options.categoryNames = [this.categoryName]
      }

      /**
       * If tracking consent is disabled for customers, `exponea` service
       * may not have been initialized. Therefore, an additional check is required.
       */
      if (!exponea.isInitialized) {
        exponea.start()
      }

      exponea.getRecommendation(options)
    },

    /**
     * Callback function for when the recommended products have been loaded.
     * @async
     * @param {Array<object>} data - An array of recommended products.
     */
    async onRecommendationsLoaded(data) {
      if (!data?.length) {
        this.destroyComponent()
        return
      }
      try {
        await this.setProducts(data)
        this.handleTrackingEvents('show')
      }
      catch (error) {
        mint.Log('error', 'Error fetching Recommended products', error, { data })
      }
    },

    /**
     * Set the products that will be displayed in the component.
     * @async
     * @param {Array<object>} data - An array of recommended products.
     */
    async setProducts(data) {
      try {
        const queries = data.map((item) => {
          return getProduct( { handle: item._handle } )
        })

        const response = await Promise.allSettled(queries)

        /**
         * If the query fails, it's likely that the product isn't
         * available on the Storefront API channel.
         */
        this.products = response.map((promise) => {
          const product = promise?.value

          if (
            promise.status !== 'fulfilled' ||
            !product?.handle ||
            product?.restricted
          ) {
            return false
          }

          return promise.value
        }).filter(Boolean)

        if (!this.products.length) {
          this.destroyComponent()
          return
        }
      }
      catch (error) {
        mint.Log('error', 'Error fetching Recommended products', error, data)
      }
    },

    /**
     * Handles recommendation events.
     * @param {String} action - Event action to track.
     */
    handleTrackingEvents(action = 'show') {
      const trackingSectionName = this.trackingParameters?.section

      const data = {
        action,
        recommendation_id: this.recommendation_id,
        data_source: trackingSectionName,
      }

      const trackedEvent = {
        event: 'recommendation',
        data,
      }

      mint.EventBus.dispatch('bloomreach:trackEvent', trackedEvent)
    },

    /**
     * Sets the state of the component based on whether it is being shown inside
     * the cart pop-out content or not.
     */
    handleCartPopupRender() {

      /**
       * Check if drawer is opened.
       */
      if (!document.body.classList.contains('open_cart')) {
        return
      }
    },

    /**
     * Remove and unmount the component. Emits an event for removing parent
     * section as well.
     *
     * @emits recommendations-not-loaded
     */
    destroyComponent() {
      const parentComponent = this.$el.closest(
        '[data-mint-component="bloomreach-recommendations"]',
      )

      this.$emit('recommendations-not-loaded')
      parentComponent?.remove()
    },
  },
}
</script>

<style src="./bloomreach-recommendations.scss" lang="scss"></style>
